/** Pixa — katalog sayfaları: Katalog, Pazaryeri Listeleri (+ aktarma), Fiyatlandırma. */
import React, { useState, useEffect } from 'react';
import {
  API, PAGE_SIZE, T, MONO, FONT, Badge, Button, Card, Col, ConfirmDangerModal, DataTable,
  Eyebrow, FilterBar, Loader, Thumb, Toast, axios, fmtDate, fmtDateTime, fmtN, inputStyle,
  money, tint, useData, useIntegrations, useLang, useList,
} from './shared';
import { takeFocus } from './focus';
import { labelOf } from './reportLabels';
import { MarketMark } from './marketplaces';
import { FindingRecords, kindInfo } from './Findings';
import { addTranslations } from '../i18n';

addTranslations({
  'Tüm Durumlar': 'All Statuses', 'Yayında': 'Live', 'Kapalı': 'Closed', 'Kilitli': 'Locked', 'Kampanyada': 'In Campaign',
  'Bağlantısız': 'Disconnected', 'Eşleşmeyen': 'Unmatched', 'Tüm Pazaryerleri': 'All Marketplaces', 'Toplu Görevler': 'Bulk Tasks',
  'Toplu Görevleri Gizle': 'Hide Bulk Tasks', 'Listeleri': 'Listings', 'Tüm listeler': 'All listings', 'BULGUDAN GELDİNİZ': 'FROM A FINDING',
  'aynı': 'same', 'kampanya': 'campaign', 'Pixa fiyatı': 'Pixa price', 'Toplam stok': 'Total stock', 'Kanal': 'Channel',
  'Kanallarda fiyat ve durum': 'Price and status by channel', "Pixa'ya göre": 'vs Pixa', 'Tüm kanallarda karşılaştır': 'Compare across channels',
  'Otomatik': 'Automatic', 'Değişiklik': 'Change', 'Yalnızca biçimlendirme (HTML) değişti.': 'Formatting (HTML) only.', 'alan daha': 'more fields',
  'Bu ürün hiçbir pazaryerinde listelenmemiş.': 'This product is not listed on any marketplace.',
  'Bu ürün tüm kanallarda aynı fiyattan satılıyor ama Pixa ana fiyatı farklı. Pixa fiyatı eski olabilir ya da kanallara bilinçli bir fiyat kuralı uygulanıyor.':
    'This product sells at the same price on every channel but its Pixa base price differs. Either the Pixa price is outdated or a deliberate channel price rule applies.',
});

// ── Katalog ──────────────────────────────────────────────────────────────────
export function ProductsPage({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const integrations = useIntegrations(clientId);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState(''); const [deb, setDeb] = useState('');
  const [active, setActive] = useState('');
  const [platform, setPlatform] = useState('');
  const [inStock, setInStock] = useState(false);
  const [image, setImage] = useState('');
  const [drawer, setDrawer] = useState<any | null>(null);
  useEffect(() => { const id = setTimeout(() => { setDeb(q); setOffset(0); }, 400); return () => clearTimeout(id); }, [q]);
  useEffect(() => { setOffset(0); }, [active, platform, inStock, image]);
  // Hata logundan "görseli olmayan ürünler" diye gelindiyse filtre hazır açılır.
  useEffect(() => { const f = takeFocus('pixa-products'); if (f?.image) setImage(f.image); }, []);

  const { rows, pagination, loading, error } = useList(clientId, '/products', {
    limit: PAGE_SIZE, offset, query: deb.trim() || undefined, isActive: active || undefined,
    platform: platform || undefined, inStock: inStock || undefined, image: image || undefined,
  });

  const cols: Col[] = [
    { key: 'name', label: 'Ürün', align: 'left', render: r => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => setDrawer(r)}>
        <Thumb url={r.thumbUrl} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: T.text, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{r.name}</div>
          <div style={{ fontSize: 10, color: T.textDim, fontFamily: MONO }}>{r.sku || ''}</div>
        </div>
      </div>) },
    { key: 'barcode', label: 'Barkod', render: r => <span style={{ fontFamily: MONO, fontSize: 11 }}>{r.barcode || '—'}</span> },
    { key: 'price', label: 'Fiyat', render: r => money(r.price, r.currency) },
    { key: 'salePrice', label: 'Satış F.', render: r => <span style={{ fontWeight: 600, color: T.text }}>{money(r.salePrice, r.currency)}</span> },
    { key: 'variantCount', label: 'Varyant', render: r => fmtN(r.variantCount) },
    { key: 'realStock', label: 'Stok', render: r => <span style={{ fontWeight: 600, color: Number(r.realStock) > 0 ? T.text : T.redText }}>{fmtN(r.realStock)}</span> },
    { key: 'platformNames', label: 'Platform', render: r => {
      const names: string[] = r.platformNames || [];
      if (!names.length) return <span style={{ color: T.textDim }}>—</span>;
      return (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' as const, justifyContent: 'flex-end' }}>
          {names.map((n: string) => <span key={n} title={n}><MarketMark name={n} size={20} /></span>)}
        </div>);
    } },
    { key: 'isActive', label: 'Durum', render: r => <Badge color={r.isActive ? T.green : T.textDim}>{r.isActive ? t('Aktif') : t('Pasif')}</Badge> },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT }}>
      <FilterBar>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('Ürün adı, SKU veya barkod')} style={{ ...inputStyle, minWidth: 240 }} />
        <select value={active} onChange={e => setActive(e.target.value)} style={{ ...inputStyle, minWidth: 120 }}>
          <option value="">{t('Durum')}: {t('Tümü')}</option>
          <option value="true">{t('Aktif')}</option>
          <option value="false">{t('Pasif')}</option>
        </select>
        <select value={platform} onChange={e => setPlatform(e.target.value)} style={{ ...inputStyle, minWidth: 160 }}>
          <option value="">{t('Platform')}: {t('Tümü')}</option>
          {integrations.map((i: any) => <option key={i.resourceId} value={i.resourceName}>{i.resourceName}</option>)}
        </select>
        <select value={image} onChange={e => setImage(e.target.value)} style={{ ...inputStyle, minWidth: 150 }}>
          <option value="">{t('Görsel')}: {t('Tümü')}</option>
          <option value="has">{t('Görseli Olanlar')}</option>
          <option value="none">{t('Görseli Olmayanlar')}</option>
        </select>
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: T.textMid, cursor: 'pointer' }}>
          <input type="checkbox" checked={inStock} onChange={e => setInStock(e.target.checked)} style={{ cursor: 'pointer' }} />
          {t('Stoksuzları Gizle')}
        </label>
      </FilterBar>
      <DataTable title={t('Katalog')} cols={cols} rows={rows} loading={loading} error={error}
        pagination={pagination} offset={offset} onOffset={setOffset} csvName="pixa-katalog.csv" minWidth={1100} />
      {drawer && <ProductDrawer clientId={clientId} product={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}

/** Ürün teşhis çekmecesi — /products/{id}/diagnose tek çağrıda her şeyi veriyor.
 *  En önemli bölüm kanal fiyat karşılaştırması: "fiyat uyuşmazlığı"nın cevabı burada. */
export function ProductDrawer({ clientId, product, onClose }: { clientId: number; product: any; onClose: () => void }) {
  const { t } = useLang();
  const integrations = useIntegrations(clientId, false);
  const { data, loading } = useData(clientId, `/products/${product.id}/diagnose`);
  const d = data || {};
  const p = d.product || {};
  const nameOf = (rid: any) => integrations.find((i: any) => String(i.resourceId) === String(rid))?.resourceName || `#${rid}`;
  const listings = (d.platformCatalog || []).map((l: any) => ({ ...l, platformName: l.platformName || nameOf(l.resourceId) }))
    .filter((l: any) => isMarketplaceName(l.platformName));
  const shelf = d.shelfStock || [];
  const base = Number(p.salePrice || p.price || 0);
  const eff = (l: any) => Math.min(...[Number(l.salePrice || 0), Number(l.campaignSalePrice || 0)].filter(x => x > 0), Infinity);
  const prices = listings.map(eff).filter((x: number) => isFinite(x));
  const allSame = prices.length >= 2 && (Math.max(...prices) - Math.min(...prices)) / Math.max(...prices) <= 0.02;
  const pixaOff = allSame && base > 0 && Math.abs(prices[0] - base) / base > 0.05;

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', justifyContent: 'flex-end', zIndex: 9998 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.surface, width: '100%', maxWidth: 580, height: '100%', overflowY: 'auto' as const, padding: '24px 26px', fontFamily: FONT, boxSizing: 'border-box' as const }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 18 }}>
          <Thumb url={product.thumbUrl} size={52} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: T.text, lineHeight: 1.3 }}>{product.name || p.name}</div>
            <div style={{ fontSize: 11, color: T.textDim, fontFamily: MONO, marginTop: 3 }}>{product.sku || p.sku}{(product.barcode || p.barcode) ? ` · ${product.barcode || p.barcode}` : ''}</div>
          </div>
          <Button variant="ghost" color={T.textDim} onClick={onClose} style={{ padding: '4px 10px', fontSize: 14 }}>✕</Button>
        </div>
        {loading ? <Loader /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0,1fr))', gap: 8 }}>
              {[['Pixa fiyatı', base ? money(base) : '—'], ['Toplam stok', fmtN(d.totalStock)], ['Kanal', fmtN(listings.length)], ['Varyant', fmtN((d.variants || []).length)]].map(([k, v]) => (
                <div key={k as string} style={{ background: 'var(--py-subtle-bg)', borderRadius: 10, padding: '10px 12px' }}>
                  <div style={{ fontSize: 10.5, color: T.textDim, fontWeight: 700 }}>{t(k as string)}</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: T.text, marginTop: 4 }}>{v}</div>
                </div>
              ))}
            </div>

            <div>
              <Eyebrow color={T.textDim}>{t('Kanallarda fiyat ve durum')}</Eyebrow>
              {pixaOff && (
                <div style={{ marginTop: 8, fontSize: 12, color: T.text, background: tint(T.blue, 10), border: `1px solid ${tint(T.blue, 40)}`, borderRadius: 10, padding: '9px 12px', lineHeight: 1.55 }}>
                  {t('Bu ürün tüm kanallarda aynı fiyattan satılıyor ama Pixa ana fiyatı farklı. Pixa fiyatı eski olabilir ya da kanallara bilinçli bir fiyat kuralı uygulanıyor.')}
                </div>
              )}
              <div style={{ marginTop: 8, border: `1px solid ${T.border}`, borderRadius: 10, overflow: 'hidden' }}>
                {listings.length === 0 ? <div style={{ fontSize: 12, color: T.textDim, padding: 12 }}>{t('Bu ürün hiçbir pazaryerinde listelenmemiş.')}</div>
                  : listings.map((l: any, i: number) => {
                    const e = eff(l); const diff = base && isFinite(e) ? (e - base) / base * 100 : null;
                    const flags = [l.closed && t('kapalı'), l.locked && t('kilitli'), l.disconnected && t('bağlantısız'), l.notMatch && t('eşleşmemiş'), !l.isActive && t('pasif')].filter(Boolean);
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 12.5, padding: '9px 12px', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                        <MarketMark name={l.platformName} size={20} />
                        <span style={{ flex: 1, minWidth: 0, color: T.text, fontWeight: 600 }}>{l.platformName}
                          {flags.length > 0 && <span style={{ marginLeft: 6, fontSize: 10.5, color: T.amberText }}>{flags.join(' · ')}</span>}
                        </span>
                        <span style={{ textAlign: 'right' as const }}>
                          <b style={{ color: T.text }}>{isFinite(e) ? money(e) : '—'}</b>
                          {l.campaignSalePrice && <span style={{ fontSize: 10, color: T.purpleText, marginLeft: 5 }}>{t('kampanya')}</span>}
                          {diff !== null && Math.abs(diff) >= 1 && <span style={{ display: 'block', fontSize: 10.5, fontWeight: 700, color: Math.abs(diff) >= 40 ? T.redText : T.amberText }}>{diff > 0 ? '+' : '−'}%{Math.abs(Math.round(diff))} {t('Pixa\'ya göre')}</span>}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>

            {shelf.length > 0 && (
              <div>
                <Eyebrow color={T.textDim}>{t('Raf stoku')}</Eyebrow>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {shelf.slice(0, 10).map((s: any, i: number) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.textMid }}>
                      <span>{s.shelfName || `Raf #${s.shelfId}`}</span><b style={{ color: T.text }}>{fmtN(s.quantity)}</b>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(d.priceMovements || []).length > 0 && (
              <div>
                <Eyebrow color={T.textDim}>{t('Son fiyat hareketleri')}</Eyebrow>
                <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {dedupeMovements(d.priceMovements).slice(0, 8).map((m: any, i: number) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: T.textMid }}>
                      <MarketMark name={nameOf(m.resourceId)} size={16} />
                      <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                        <b style={{ color: T.text }}>{nameOf(m.resourceId)}</b> · {m.transactionResource || '—'}
                      </span>
                      <span style={{ flexShrink: 0 }}><b style={{ color: T.text }}>{money(m.salePrice)}</b> · {fmtDate(m.creationDate)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/** Pixa fiyat hareketlerini her varyant için ayrı yazıyor (aynı an, aynı fiyat, 6 satır). */
function dedupeMovements(ms: any[]) {
  const seen = new Set<string>();
  return ms.filter(m => { const k = `${m.resourceId}|${m.salePrice}|${String(m.creationDate).slice(0, 16)}`; if (seen.has(k)) return false; seen.add(k); return true; });
}

const isMarketplaceName = (n: string) => !/nebim|logo|mikro|netsis|erp|aras|yurti|mng|ptt kargo|sürat|surat|ups|dhl|kargo|hepsijet/i.test(n || '');

// ── Pazaryeri Listeleri ──────────────────────────────────────────────────────

// Pixa'nın platform-catalog ucunun kabul ettiği status değerleri (sunucu doğruluyor).
const STATUS_OPTS: [string, string][] = [
  ['', 'Tüm Durumlar'], ['connected', 'Yayında'], ['closed', 'Kapalı'], ['locked', 'Kilitli'],
  ['campaign', 'Kampanyada'], ['notConnected', 'Bağlantısız'], ['notMatch', 'Eşleşmeyen'],
];
const KIND_STATUS: Record<string, string> = {
  listing_closed: 'closed', listing_locked: 'locked', listing_not_matched: 'notMatch', listing_disconnected: 'notConnected',
};
const EMPTY_BY_STATUS: Record<string, string> = {
  notMatch: 'Eşleşmeyen listing yok. Not: hata kayıtlarındaki "… eşleşmeyen ürünler", pazaryerinde olup Pixa kataloğunda karşılığı bulunmayan ürünlerdir; Pixa bunları listing olarak tutmadığı için bu tabloda görünmezler. Çözüm için Asistan > Bulgular\'daki "Eşleştir" görevini kullanın.',
  closed: 'Bu filtrede kapalı listing yok.', locked: 'Bu filtrede kilitli listing yok.',
  campaign: 'Kampanyadaki listing yok.', notConnected: 'Bağlantısız listing yok.',
};

export function ListingsPage({ clientId }: { clientId: number }) {
  const { t } = useLang();
  const integrations = useIntegrations(clientId);
  const [offset, setOffset] = useState(0);
  const [resourceId, setResourceId] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [task, setTask] = useState<{ platform: string; action: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [note, setNote] = useState<{ msg: string; tone: 'ok' | 'err' }>({ msg: '', tone: 'ok' });
  const [finding, setFinding] = useState<any | null>(null);
  const [drawer, setDrawer] = useState<any | null>(null);
  const [showTasks, setShowTasks] = useState(false);
  const [focus] = useState(() => takeFocus('pixa-listings'));

  useEffect(() => { setOffset(0); setSelected(new Set()); }, [resourceId, status]);

  // Bulgudan gelindiyse: bulgunun kendisi (etkilenen kayıtlarla) üstte gösterilir,
  // pazaryeri/durum filtresi hazır uygulanır.
  useEffect(() => {
    if (!focus) return;
    const st = focus.status || (focus.kind ? KIND_STATUS[focus.kind] : '');
    if (st) setStatus(st);
    if ((focus as any).groupId) {
      axios.get(`${API}/pixa/${clientId}/anomalies/${(focus as any).groupId}`).then(r => setFinding(r.data)).catch(() => { });
    }
  }, [focus, clientId]);
  useEffect(() => {
    if (!focus?.platform || !integrations.length || resourceId) return;
    const hit = integrations.find((i: any) => String(i.resourceName) === focus.platform);
    if (hit) setResourceId(String(hit.resourceId));
  }, [integrations, focus]); // eslint-disable-line react-hooks/exhaustive-deps

  const { rows, pagination, loading, error, reload } = useList(clientId, '/products/platform-catalog', {
    limit: PAGE_SIZE, offset, resourceId: resourceId || undefined, status: status || undefined,
  });

  const selRows = rows.filter((r: any) => selected.has(r.id));
  const productIds = Array.from(new Set(selRows.map((r: any) => r.productId).filter(Boolean)));

  const runTask = async () => {
    if (!task) return;
    setBusy(true); setErr(null);
    const push = ['sendProducts', 'updatePrice', 'updateInventory'].includes(task.action);
    try {
      await axios.post(`${API}/pixa/${clientId}/integrations/market-task`, {
        platform: task.platform, action: task.action,
        ...(push && productIds.length ? { productIds } : {}),
        ...(push ? { confirm: 'ONAYLA' } : {}),
      });
      setTask(null); setSelected(new Set());
      setNote({ msg: t('Görev Pixa\'ya iletildi.'), tone: 'ok' });
      setTimeout(reload, 1500);
    } catch (e: any) { setErr(e?.response?.data?.detail || t('Hata')); }
    finally { setBusy(false); setTimeout(() => setNote({ msg: '', tone: 'ok' }), 5000); }
  };

  const runPull = async (platform: string, action: string) => {
    setBusy(true);
    try {
      await axios.post(`${API}/pixa/${clientId}/integrations/market-task`, { platform, action });
      setNote({ msg: t('Görev Pixa\'ya iletildi.'), tone: 'ok' });
    } catch (e: any) { setNote({ msg: e?.response?.data?.detail || t('Hata'), tone: 'err' }); }
    finally { setBusy(false); setTimeout(() => setNote({ msg: '', tone: 'ok' }), 5000); }
  };

  const activeIntegrations = integrations.filter((i: any) => i.active);
  const selectedPlatform = integrations.find((i: any) => String(i.resourceId) === resourceId)?.resourceName || '';
  const TASK_LABEL: Record<string, string> = { sendProducts: 'Ürün Gönder', updatePrice: 'Fiyat Gönder', updateInventory: 'Stok Gönder' };

  const cols: Col[] = [
    { key: 'productName', label: 'Ürün', align: 'left', render: r => (
      <div onClick={() => setDrawer({ id: r.productId, name: r.productName || r.name, sku: r.productSku, thumbUrl: r.thumbUrl })}
        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} title={t('Tüm kanallarda karşılaştır')}>
        <Thumb url={r.thumbUrl} size={32} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: T.text, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{r.productName || r.name}</div>
          <div style={{ fontSize: 10.5, color: T.textDim, fontFamily: MONO }}>{r.productSku || ''}{r.mpId ? ` · ${r.mpId}` : ''}</div>
        </div>
      </div>) },
    { key: 'platformName', label: 'Pazaryeri', align: 'left', render: r => (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' as const }}>
        <MarketMark name={r.platformName} size={18} />{r.platformName || `#${r.resourceId}`}
      </span>) },
    { key: 'effectivePrice', label: 'Pazaryeri F.', render: r => (
      <span style={{ whiteSpace: 'nowrap' as const }}>
        {Number(r.listPrice) > Number(r.effectivePrice || 0) && <s style={{ color: T.textFaint, fontSize: 10.5, marginRight: 5 }}>{money(r.listPrice)}</s>}
        <b style={{ color: T.text }}>{r.effectivePrice ? money(r.effectivePrice) : '—'}</b>
        {r.campaignSalePrice && <span style={{ display: 'block', fontSize: 10, color: T.purpleText }}>{t('kampanya')}</span>}
      </span>) },
    { key: 'pixaPrice', label: 'Pixa F.', render: r => r.pixaPrice ? money(r.pixaPrice) : '—' },
    { key: 'priceDiffPct', label: 'Fark', render: r => {
      const d = r.priceDiffPct;
      if (d === null || d === undefined) return <span style={{ color: T.textFaint }}>—</span>;
      if (Math.abs(d) < 1) return <span style={{ color: T.greenText, fontSize: 11 }}>{t('aynı')}</span>;
      return <b style={{ color: Math.abs(d) >= 40 ? T.redText : T.amberText }}>{d > 0 ? '+' : '−'}%{Math.abs(d).toLocaleString('tr-TR')}</b>;
    } },
    { key: 'pixaStock', label: 'Stok (Pixa)', render: r => (
      <span style={{ fontWeight: 600, color: Number(r.pixaStock) > 0 ? T.text : T.redText }}>{fmtN(r.pixaStock)}</span>) },
    { key: 'durum', label: 'Durum', render: r => {
      const bad = [r.notMatch && t('eşleşmemiş'), r.disconnected && t('bağlantısız'), r.closed && t('kapalı'),
                   r.locked && t('kilitli'), !Number(r.isActive) && t('pasif')].filter(Boolean);
      return bad.length ? <Badge color={T.amber}>{bad.join(' · ')}</Badge> : <Badge color={T.green}>{t('Yayında')}</Badge>;
    } },
  ];

  const fInfo = finding ? kindInfo(finding.kind) : null;
  const fPlatform = finding ? (finding.scope || '').split('·')[0].trim() : '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT }}>
      {finding && fInfo && (
        <Card pad={18} style={{ borderColor: tint(T.accent, 45) }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12 }}>
            {fPlatform && <MarketMark name={fPlatform} size={30} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 10.5, fontWeight: 800, color: T.amberDeepText, letterSpacing: '0.08em' }}>{t('BULGUDAN GELDİNİZ')}</div>
              <div style={{ fontSize: 14.5, fontWeight: 800, color: T.text, margin: '3px 0 4px' }}>{t(fInfo.title)}{fPlatform ? ` · ${fPlatform}` : ''} <span style={{ color: T.textDim, fontWeight: 600 }}>({fmtN(finding.count)})</span></div>
              <div style={{ fontSize: 12.5, color: T.textMid, lineHeight: 1.55 }}>{fInfo.meaning(fPlatform, finding)} {fInfo.todo(fPlatform, finding, finding.sample || [])[0]}</div>
            </div>
            <Button variant="ghost" color={T.textDim} onClick={() => setFinding(null)} style={{ padding: '5px 10px', fontSize: 11.5 }}>{t('Tüm listeler')}</Button>
          </div>
          <FindingRecords kind={finding.kind} records={finding.sample || []} platform={fPlatform} />
        </Card>
      )}

      <FilterBar>
        <select value={resourceId} onChange={e => setResourceId(e.target.value)} style={{ ...inputStyle, minWidth: 170 }}>
          <option value="">{t('Tüm Pazaryerleri')}</option>
          {integrations.map((i: any) => <option key={i.resourceId} value={i.resourceId}>{i.resourceName}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ ...inputStyle, minWidth: 160 }}>
          {STATUS_OPTS.map(([v, l]) => <option key={v} value={v}>{t(l)}</option>)}
        </select>
        {selected.size > 0 && <div style={{ fontSize: 12, color: T.textMid }}>{selected.size} {t('seçili')} · {productIds.length} {t('ürün')}</div>}
        <Button variant="ghost" color={T.accent} onClick={() => setShowTasks(v => !v)} style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: 11.5 }}>
          {showTasks ? t('Toplu Görevleri Gizle') : t('Toplu Görevler')}
        </Button>
      </FilterBar>

      {showTasks && (
        <Card pad={18}>
          <div style={{ fontSize: 13, fontWeight: 800, color: T.text }}>{t('Toplu Görevler')}</div>
          <div style={{ fontSize: 12, color: T.textMid, margin: '4px 0 12px', lineHeight: 1.6 }}>
            {t('"Çek" görevleri pazaryerindeki veriyi Pixa\'ya alır, bir şeyi değiştirmez. "Gönder" görevleri pazaryerindeki yayını değiştirir ve onay ister; tabloda ürün seçtiyseniz yalnızca seçili ürünler gönderilir.')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {activeIntegrations.filter((i: any) => i.isMarketplace !== false).map((i: any) => (
              <div key={i.resourceId} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const, borderTop: `1px solid ${T.border}`, paddingTop: 10 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 130, fontSize: 12.5, fontWeight: 700, color: T.text }}>
                  <MarketMark name={i.resourceName} size={20} />{i.resourceName}
                </span>
                {[['fetchProducts', 'Ürünleri Çek'], ['updateOrders', 'Siparişleri Çek'], ['matchProducts', 'Eşleştir'], ['fetchClaims', 'İadeleri Çek']].map(([a, lbl]) => (
                  <Button key={a} variant="ghost" color={T.accent} disabled={busy} onClick={() => runPull(i.resourceName, a)} style={{ padding: '4px 10px', fontSize: 10.5 }}>{t(lbl)}</Button>
                ))}
                {Object.entries(TASK_LABEL).map(([a, lbl]) => (
                  <Button key={a} variant="soft" color={T.red} disabled={busy} onClick={() => { setErr(null); setTask({ platform: i.resourceName, action: a }); }}
                    style={{ padding: '4px 10px', fontSize: 10.5 }}>{t(lbl)}</Button>
                ))}
              </div>
            ))}
          </div>
          <div style={{ marginTop: 10 }}><Toast msg={note.msg} tone={note.tone} /></div>
        </Card>
      )}

      <DataTable title={selectedPlatform ? `${selectedPlatform} ${t('Listeleri')}` : t('Pazaryeri Listeleri')}
        subtitle={t('Fark: pazaryerindeki geçerli fiyatın (kampanya varsa kampanya fiyatı) Pixa ana satış fiyatına göre farkı. Ürüne tıklayınca tüm kanallardaki fiyatı yan yana görürsünüz.')}
        empty={status ? t(EMPTY_BY_STATUS[status] || 'Kayıt bulunamadı.') : undefined}
        cols={cols} rows={rows} loading={loading} error={error}
        pagination={pagination} offset={offset} onOffset={setOffset} csvName="pixa-listingler.csv" minWidth={1000}
        selectable selected={selected}
        onToggle={id => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; })}
        onToggleAll={() => setSelected(s => s.size === rows.length ? new Set() : new Set(rows.map((r: any) => r.id)))} />

      <ConfirmDangerModal open={!!task} busy={busy} error={err}
        title={task ? `${task.platform} — ${t(TASK_LABEL[task.action] || task.action)}` : ''}
        description={productIds.length
          ? `${productIds.length} ${t('ürün için görev tetiklenecek. Değişiklik pazaryerindeki yayına yansır.')}`
          : t('Seçili ürün yok — entegrasyonun varsayılan ürün seti gönderilecek. Değişiklik pazaryerindeki yayına yansır.')}
        items={selRows.slice(0, 12).map((r: any) => `${r.productSku || ''} · ${r.productName || r.name}`)}
        onCancel={() => setTask(null)} onConfirm={runTask} />
      {drawer && <ProductDrawer clientId={clientId} product={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}

// ── Değişiklik Geçmişi ──────────────────────────────────────────────────────

const stripHtml = (x: string) => x.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
/** Cümle sonu noktası — metin zaten noktalama ya da "…" ile bitiyorsa eklenmez. */
const dot = (x: string) => /[.!?…:]$/.test(x.trim()) ? x : `${x}.`;

export function PricingPage({ clientId, start, end }: { clientId: number; start?: string; end?: string }) {
  const { t } = useLang();
  const integrations = useIntegrations(clientId, false);
  const [offset, setOffset] = useState(0);
  const [drawer, setDrawer] = useState<any | null>(null);
  useEffect(() => { setOffset(0); }, [start, end]);

  // Değişikliğin kaynağı: bir Pixa kullanıcısı yaptıysa (operatorName/UserId dolu) "Pixa";
  // entegrasyon senkronundan geldiyse açıklamada geçen kanal (ör. Shopify); ikisi de
  // yoksa "Otomatik". Bugünkü kayıtların tamamı Pixa kullanıcılarına ait.
  const sourceOf = (r: any): string => {
    if (r.operatorUserId || r.operatorName) return 'Pixa';
    const c = String(r.comment || '').toLocaleLowerCase('tr');
    const hit = integrations.find((i: any) => c && c.includes(String(i.resourceName).toLocaleLowerCase('tr')));
    return hit ? hit.resourceName : t('Otomatik');
  };
  const { rows, pagination, loading, error } = useList(clientId, '/products/change-history', {
    limit: PAGE_SIZE, offset, start_date: start, end_date: end,
  });

  // Pixa oldValue/newValue alanlarını NESNE olarak döndürüyor ({fullDescription: "...",
  // stockUpdatedOn: "..."} gibi). Doğrudan basmak React'i çökertiyordu (beyaz ekran) —
  // nesneler alan adlarına ayrıştırılıp okunabilir metne çevriliyor.
  const keysOf = (r: any): string[] => {
    const o = r.oldValue, n = r.newValue;
    if (o && typeof o === 'object' && n && typeof n === 'object') {
      return Array.from(new Set([...Object.keys(o), ...Object.keys(n)]))
        .filter(k => k !== 'stockUpdatedOn' && JSON.stringify(o[k]) !== JSON.stringify(n[k]));
    }
    return [r.field || r.fieldName].filter(Boolean);
  };
  const val = (v: any): string => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? t('Evet') : t('Hayır');
    if (typeof v === 'object') return JSON.stringify(v);
    return stripHtml(String(v));
  };
  const clip = (x: string, n = 70) => x.length > n ? x.slice(0, n) + '…' : x;

  const change = (r: any) => {
    const keys = keysOf(r);
    const o = r.oldValue || {}, n = r.newValue || {};
    if (!keys.length) return <span style={{ color: T.textDim }}>—</span>;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {keys.slice(0, 3).map(k => {
          const a = typeof o === 'object' ? o[k] : o, b = typeof n === 'object' ? n[k] : n;
          const onlyFormat = typeof a === 'string' && typeof b === 'string' && a !== b && stripHtml(a) === stripHtml(b);
          return (
            <div key={k} style={{ fontSize: 11.5, lineHeight: 1.5 }}>
              <b style={{ color: T.text }}>{labelOf(k)}:</b>{' '}
              {onlyFormat ? <span style={{ color: T.textDim }}>{t('Yalnızca biçimlendirme (HTML) değişti.')}</span> : <>
                <span style={{ color: T.textDim, textDecoration: 'line-through' }} title={val(a)}>{clip(val(a), 40)}</span>
                <span style={{ color: T.textFaint }}> → </span>
                <span style={{ color: T.text }} title={val(b)}>{dot(clip(val(b), 60))}</span>
              </>}
            </div>
          );
        })}
        {keys.length > 3 && <span style={{ fontSize: 10.5, color: T.textDim }}>{dot(`+${keys.length - 3} ${t('alan daha')}`)}</span>}
      </div>
    );
  };

  const cols: Col[] = [
    { key: 'productName', label: 'Ürün', align: 'left', render: r => (
      <div onClick={() => setDrawer({ id: r.productId, name: r.productName, sku: r.productSku, thumbUrl: r.thumbUrl })}
        style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', minWidth: 220 }}>
        <Thumb url={r.thumbUrl} size={32} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, color: T.text, maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{r.productName || `#${r.productId}`}</div>
          <div style={{ fontSize: 10.5, color: T.textDim, fontFamily: MONO }}>{r.productSku || ''}</div>
        </div>
      </div>) },
    { key: 'scope', label: 'Kanal', align: 'left', render: r => {
      const src = sourceOf(r);
      return src === 'Pixa' ? (
        <span title={t('Pixa ana ürün kartında yapıldı; ürünün bağlı olduğu tüm kanallara yansır.')}
          style={{ fontSize: 11, fontWeight: 700, color: T.blueText, background: tint(T.blue, 12), borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap' as const }}>Pixa</span>
      ) : (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap' as const }}><MarketMark name={src} size={18} />{src}</span>
      );
    } },
    { key: 'change', label: 'Değişiklik', align: 'left', render: change },
    { key: 'operatorName', label: 'Kullanıcı', render: r => r.operatorName || '—' },
    { key: 'creationDate', label: 'Tarih', render: r => fmtDateTime(r.creationDate) },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT }}>
      <DataTable title={t('Değişiklik Geçmişi')}
        subtitle={t('Pixa\'daki ana ürün kartında yapılan değişiklikler (açıklama, taslak durumu, fiyat…). Bunlar tek bir pazaryerine ait değildir, ürünün yayında olduğu tüm kanallara yansır. Kanal bazlı fiyat hareketleri için ürüne tıklayın.')}
        cols={cols} rows={rows} loading={loading} error={error}
        pagination={pagination} offset={offset} onOffset={setOffset} csvName="pixa-degisiklik-gecmisi.csv" minWidth={1000} />
      {drawer && <ProductDrawer clientId={clientId} product={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}
